import React, { ComponentType, useEffect, useState } from "react"
import { compose } from "@reduxjs/toolkit"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { FormikHelpers as FormikActions } from "formik"
import { authenticate } from "@web-eid/web-eid-library/web-eid"

import { useAppDispatch } from "redux/hooks"
import { withVerification } from "hocs/features"
import {
  fetchIdCardNonce,
  makeIdCardAuthentication,
} from "redux/features/auth/idCardThunk"
import { Content, Card } from "pages/sessions/elements"
import { showAlert } from "redux/features/notice/noticeSlice"
import { ID_CARD_ERRORS } from "config/auth"
import IdCardForm from "components/Sessions/IdCard/Form"
import Title from "components/Sessions/Title"
import { ClientIdCardFormValues } from "types"

const IdCardPage = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [nonce, setNonce] = useState("")
  const [sessionId, setSessionId] = useState("")

  useEffect(() => {
    dispatch(fetchIdCardNonce())
      .then(({ payload }) => {
        const { authentication } = payload

        setSessionId(authentication.session_id)
        setNonce(authentication.nonce)
      })
      .catch(() => {
        dispatch(
          showAlert({ message: t("notice.sessions.id_card.unavailable") }),
        )
      })
  }, [])

  const handleSubmit = async (
    values: ClientIdCardFormValues,
    actions: FormikActions<object>,
  ) => {
    try {
      // NOTE: https connection is required by web-eid (use ngrok http http://localhost:3002)
      const payload = await authenticate(nonce, { lang: "en" })

      dispatch(
        makeIdCardAuthentication({ session_id: sessionId, payload: payload }),
      )
        .then(() => {
          navigate("/")
        })
        .catch((error) => {
          dispatch(showAlert({ message: error }))
          actions.setSubmitting(false)
        })
    } catch ({ code }) {
      if (code && ID_CARD_ERRORS.includes(code.toString())) {
        dispatch(
          showAlert({
            message: t(
              `notice.sessions.id_card.${code.toString().toLowerCase()}`,
            ),
          }),
        )
      }
    }
  }

  return (
    <Content data-testid="login-id-card-page">
      <Card elevation={0}>
        <Title back />
        <IdCardForm initialValues={{ terms: true }} onSubmit={handleSubmit} />
      </Card>
    </Content>
  )
}

export default compose<ComponentType>(withVerification)(IdCardPage)
